import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import { Send, Person, Check, DoneAll, ArrowBack } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { setAdminConversations } from "../../../redux/actions/admin";
import useAdminAPIClient from "../../../api/admin/hooks/useAdminAPIClient";

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
};

const MessageBubble = ({ message, isAdmin }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isAdmin ? "flex-end" : "flex-start",
        mb: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: "70%",
          bgcolor: isAdmin ? "#197b68" : "#f5f5f5",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        {message.subject && (
          <Box
            sx={{
              bgcolor: isAdmin ? "#004a3c" : "#e0e0e0",
              px: 2,
              py: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: "bold",
                mb: "0 !important",
                color: isAdmin ? "white !important" : "text.primary",
              }}
            >
              {message.subject}
            </Typography>
          </Box>
        )}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              mb: "0 !important",
              color: isAdmin ? "white !important" : "text.primary",
            }}
          >
            {message.message}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isAdmin
                  ? "rgba(255,255,255,0.7) !important"
                  : "text.secondary",
                mb: "0 !important",
              }}
            >
              {formatTime(message.sentAt)}
            </Typography>
            {isAdmin &&
              (message.read ? (
                <DoneAll
                  sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}
                />
              ) : (
                <Check sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ConversationView = ({ conversationId, onBack }) => {
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const { conversations, users } = useSelector((state) => state.admin);
  const { sendMessageToUser, getConversations, markMessagesAsRead } =
    useAdminAPIClient();
  const dispatch = useDispatch();

  const messages = conversations?.[conversationId] || [];
  const user = users?.find((u) => u.userId === conversationId);
  const displayName =
    user?.displayName ||
    user?.name ||
    user?.email?.split("@")[0] ||
    conversationId?.substring(0, 8) + "...";

  // Mark unread user messages as read when conversation is opened
  useEffect(() => {
    if (!conversationId) return;

    const markUnreadAsRead = async () => {
      // Get current messages from conversations state
      const currentMessages = conversations?.[conversationId] || [];
      const unreadUserMessages = currentMessages.filter(
        (msg) => msg.senderType === "user" && !msg.read,
      );

      if (unreadUserMessages.length > 0) {
        const messageIds = unreadUserMessages.map((msg) => msg.messageId);
        await markMessagesAsRead(messageIds);
        // Refresh to update the read status
        const data = await getConversations();
        if (data?.conversations) {
          dispatch(setAdminConversations(data.conversations));
        }
      }
    };

    markUnreadAsRead();
    // Only run when conversationId changes, not on every message update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.sentAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await sendMessageToUser({
        userId: conversationId,
        message: newMessage.trim(),
        subject: "", // No subject for replies
      });
      setNewMessage("");
      // Refresh conversations to get new message
      const data = await getConversations();
      if (data?.conversations) {
        dispatch(setAdminConversations(data.conversations));
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  if (!conversationId) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafa",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Select a conversation to view messages
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          bgcolor: "#004a3c",
          color: "white",
          borderRadius: "8px 8px 0 0",
        }}
      >
        {onBack && (
          <IconButton onClick={onBack} sx={{ color: "white" }}>
            <ArrowBack />
          </IconButton>
        )}
        <Avatar sx={{ bgcolor: "#197b68" }}>
          <Person />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: "0 !important", color: "white !important" }}
          >
            {displayName}
          </Typography>
          {user?.email && (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.8,
                mb: "0 !important",
                color: "white !important",
              }}
            >
              {user.email}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${messages.length} messages`}
          size="small"
          sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
        />
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          bgcolor: "#fafafa",
        }}
        className="scroller"
      >
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <Box key={date}>
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <Chip
                label={date}
                size="small"
                sx={{ bgcolor: "white", fontSize: "0.75rem" }}
              />
            </Box>
            {msgs.map((message) => (
              <MessageBubble
                key={message.messageId}
                message={message}
                isAdmin={message.senderType === "admin" || !message.senderType}
              />
            ))}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          p: 2,
          bgcolor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          borderRadius: "0 0 8px 8px",
        }}
      >
        <TextField
          fullWidth
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          multiline
          maxRows={3}
          size="small"
          disabled={sending}
        />
        <IconButton
          color="primary"
          onClick={handleSendMessage}
          disabled={!newMessage.trim() || sending}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            bgcolor: "#197b68",
            color: "white !important",
            "&:hover": { bgcolor: "#004a3c" },
            "&:disabled": { bgcolor: "grey.300", color: "grey.500 !important" },
            "& svg": { color: "white !important" },
          }}
        >
          <Send sx={{ color: "white !important" }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ConversationView;
